<template>
  <div v-if="store.loaded" class="Cart" :class="{ 'Cart--Empty': isEmpty }">
    <CartEmpty v-if="isEmpty" />
    <CartSummary
      v-show="!isEmpty"
      :items="items"
      :show-free-shipping-copy="showFreeShippingTooltip"
    />
    <CartReceipt
      v-show="!isEmpty"
      :items="items"
      :show-free-shipping-tooltip="showFreeShippingTooltip"
    />
    <Upsell
      v-if="upsellIsReady"
      v-show="!isEmpty"
      :selection-listener="false"
      :items="store.upsell.items"
      :title="upsellTitle"
      :dy-decision-id="store.upsell.decisionId"
      :dy-variation-id="store.upsell.variationId"
      location="upsell"
    />
    <CartNudge v-show="!isEmpty" />
    <CartCheckout v-show="!isEmpty" />
    <CartBrokenPopup v-show="showBrokenCartPopup && !isEmpty" />
  </div>
  <Interstitial />
</template>

<script setup>
import { onMounted, computed, watch } from 'vue';
import { useCartStore } from './Composables/Store';
import { pubSub } from '@utils/PubSub';
import { emailDlPush } from '@utils/global-functions';
import CartEmpty from './CartEmpty.vue';
import CartNudge from './CartNudge.vue';
import CartCheckout from './CartCheckout.vue';
import CartSummary from './CartSummary.vue';
import CartReceipt from './CartReceipt.vue';
import CartBrokenPopup from './CartBrokenPopup.vue';
import Interstitial from './Interstitial.vue';
import Upsell from '../Global/Upsell.vue';

const props = defineProps({
  terms: {
    type: Object,
    default: () => ({}),
  },
  freeShipThresh: {
    type: Number,
    default: 0,
  },
  quantityPolicy: {
    type: Number,
    default: 10,
  },
  upsellItems: {
    type: String,
    default: '',
  },
  receiveRecommendations: {
    type: [String, Boolean],
    default: false,
  },
  showFreeShippingTooltip: {
    type: [String, Boolean],
    default: false,
  },
});

const store = useCartStore();

store.fetchWindow();
store.cartListeners();

const items = computed(() => store.getLineItems);
const upsellIsReady = computed(() => store.upsellIsReady);
const upsellTitle = computed(
  () => store.upsell.title ?? store.terms?.upsell?.title
);
const isEmpty = computed(() => {
  return store.loaded && store.getLineItems.length <= 0;
});
const receiveRecommendations =
  JSON.parse(props?.receiveRecommendations) ?? false;

const handleRecs = () => {
  pubSub.unsubscribe('theme:cart:upsell:init', handleRecs, handleFallbackRecs);
  if (!receiveRecommendations) return;
  handleUpsell(true);
};

const handleFallbackRecs = () => {
  pubSub.unsubscribe('theme:cart:upsell:init', handleRecs, handleFallbackRecs);
  handleUpsell();
};

let upsellFetching = false;
const handleUpsell = async (overwritten = false) => {
  if (window.DY?.recommendationContext?.type !== 'CART') return;
  if (upsellFetching) return;
  upsellFetching = true;
  store.$state.upsell.loaded = false;
  store.$state.upsell.items = await store.fetchUpsellItems();
  store.$state.upsell.loaded = true;
  store.$state.upsell.overwritten = overwritten;
  upsellFetching = false;
};

watch(() => items, store.onChange, { deep: true });
pubSub.subscribe('theme:cart:upsell:init', handleRecs, handleFallbackRecs);
pubSub.subscribe('theme:cart:upsell:fetch', () => handleUpsell());

const showBrokenCartPopup = computed(() => {
  return store.$state.show_broken_cart_popup;
});
onMounted(async () => {
  await store.checkToteStatus();
  const data = await store.fetchEndpoint();
  store.$state = { ...store.$state, ...data };
  store.$state.quantity_policy = props.quantityPolicy;
  store.$state.terms = props.terms;
  store.$state.free_ship_thresh = props.freeShipThresh;
  store.$state.loaded = true;
  store.$state.upsell.allow_overwrite = receiveRecommendations;
  store.$state.upsell.items = [];
  await store.setAttributes();
  emailDlPush();
});
</script>
